import React, { useState } from 'react';
import { Modal } from '../components/ui/Modal';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { usePlanos } from '../hooks/usePlanos';
import { usePacientes } from '../hooks/usePacientes';
import { useAuth } from '../contexts/AuthContext';
import { createPlan, updatePlan } from '../services/dataService';
import { CreateMealPlanModal } from '../components/plan/CreateMealPlanModal';
import { generateMealPlanPDF } from '../utils/mealPlanPdfExport';
import { sendPDFToPatient } from '../services/pdfSendService';
import { PDFSendModal } from '../components/pdf/PDFSendModal';
import { database } from '../config/backend';
import { ref, get } from '../services/phpDataAdapter';
import { EditableMealPlan, normalizeMealPlan } from '../utils/mealPlanData';
import './Pages.css';

export const Plans: React.FC = () => {
  const { user } = useAuth();
  const { planos, loading, error, refresh } = usePlanos();
  const { pacientes } = usePacientes();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [patientId, setPatientId] = useState<string | null>(null);
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [initialPlan, setInitialPlan] = useState<EditableMealPlan | null>(null);
  const [busyPlanId, setBusyPlanId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [history, setHistory] = useState<Array<{ revision: number; plan: EditableMealPlan }> | null>(null);
  const showHistory = async (id: string, ownerId: string) => {
    setBusyPlanId(id);
    setActionError(null);
    try {
      const snapshot = await get(ref(database, `mealPlans/${ownerId}/${id}`));
      if (!snapshot.exists()) throw new Error('Plano não encontrado.');
      const raw = snapshot.val();
      const versions = [raw, ...Object.values(raw.versions || {})] as Array<Record<string, any>>;
      setHistory(versions.map(value => ({ revision: Number(value.revision ?? 0), plan: normalizeMealPlan(value) })).sort((a, b) => b.revision - a.revision));
    } catch (error) { setActionError(error instanceof Error ? error.message : 'Não foi possível carregar o histórico.'); }
    finally { setBusyPlanId(null); }
  };
  const [pdf, setPdf] = useState<{ blob: Blob; patientId: string; fileName: string } | null>(null);
  const patientName = (id: string) => pacientes.find(p => p.id === id)?.nome || 'Paciente';
  const readPlan = async (id: string, ownerId: string) => {
    const snapshot = await get(ref(database, `mealPlans/${ownerId}/${id}`));
    if (!snapshot.exists()) throw new Error('Plano não encontrado. Atualize a lista.');
    return normalizeMealPlan(snapshot.val());
  };
  const closeEditor = () => {
    setIsModalOpen(false);
    setEditingPlanId(null);
    setInitialPlan(null);
    setPatientId(null);
  };
  const openNew = () => { closeEditor(); setIsModalOpen(true); };
  const edit = async (id: string, ownerId: string) => {
    setBusyPlanId(id);
    setActionError(null);
    try {
      const plan = await readPlan(id, ownerId);
      setPatientId(ownerId);
      setInitialPlan(plan);
      setEditingPlanId(id);
      setIsModalOpen(true);
    } catch (e) { setActionError(e instanceof Error ? e.message : 'Não foi possível abrir o plano.'); }
    finally { setBusyPlanId(null); }
  };
  const save = async (ownerId: string, data: EditableMealPlan) => {
    if (!user) throw new Error('Entre novamente para salvar o plano.');
    if (editingPlanId) await updatePlan(user.uid, ownerId, editingPlanId, data);
    else await createPlan(user.uid, ownerId, data);
    refresh();
  };
  const generatePDF = async (id: string, ownerId: string) => {
    if (!user) return;
    setBusyPlanId(id);
    setActionError(null);
    try {
      const plan = await readPlan(id, ownerId);
      const blob = await generateMealPlanPDF({ ...plan, name: plan.nome, goal: plan.objetivo, notes: plan.observacoes }, patientName(ownerId), user.displayName || user.email || 'Nutricionista');
      const today = new Date();
      const date = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      setPdf({ blob, patientId: ownerId, fileName: `plano_alimentar_${patientName(ownerId).replace(/[^\p{L}\d_-]+/gu, '_')}_${date}.pdf` });
    } catch (e) { setActionError(e instanceof Error ? e.message : 'Não foi possível gerar o PDF.'); }
    finally { setBusyPlanId(null); }
  };
  return <div className="ns-page">
    <div className="ns-page-header">
      <div><h2>Planos de refeição</h2><p>Crie, edite e exporte os planos alimentares dos seus pacientes.</p></div>
      <div className="ns-page-header-actions"><Button onClick={openNew}>Novo plano</Button></div>
    </div>
    {(error || actionError) && <p role="alert">{actionError || error}</p>}
    <Card title="Planos recentes" subtitle="Últimos planos criados">
      <div className="ns-table-wrapper"><table className="ns-table">
        <thead><tr><th>Paciente</th><th>Nome do plano</th><th>Duração</th><th>Atualizado em</th><th>Ações</th></tr></thead>
        <tbody>
          {loading && <tr><td colSpan={5}>Carregando planos...</td></tr>}
          {!loading && planos.length === 0 && <tr><td colSpan={5}>Nenhum plano encontrado.</td></tr>}
          {!loading && planos.map(plan => <tr key={`${plan.pacienteId}-${plan.id}`}>
            <td>{patientName(plan.pacienteId)}</td><td>{plan.nome}</td>
            <td>{plan.duracaoSemanas ? `${plan.duracaoSemanas} semanas` : '-'}</td>
            <td>{new Date(plan.atualizadoEm).toLocaleDateString('pt-BR')}</td>
            <td><div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <Button variant="secondary" disabled={busyPlanId !== null} onClick={() => edit(plan.id, plan.pacienteId)}>Editar</Button>
              <Button variant="secondary" disabled={busyPlanId !== null} onClick={() => showHistory(plan.id, plan.pacienteId)}>Histórico</Button>
              <Button variant="secondary" disabled={busyPlanId !== null} onClick={() => generatePDF(plan.id, plan.pacienteId)}>PDF</Button>
            </div></td>
          </tr>)}
        </tbody>
      </table></div>
    </Card>
    <Modal isOpen={!!history} onClose={() => setHistory(null)} title="Histórico do plano" size="lg">
      <p>A primeira versão é a atual; as anteriores são somente para consulta.</p>
      {history?.map(({ revision, plan }, index) => <details key={revision} open={index === 0}>
        <summary>Versão {revision} — {plan.nome}{index === 0 ? ' (atual)' : ''}</summary>
        <p>{plan.objetivo} · {plan.hydration}</p>
        {Object.entries(plan.weeklyPlan).map(([day, meals]) => <section key={day}><h4>{({ monday: 'Segunda', tuesday: 'Terça', wednesday: 'Quarta', thursday: 'Quinta', friday: 'Sexta', saturday: 'Sábado', sunday: 'Domingo' } as Record<string, string>)[day]}</h4>
          {Object.entries(meals).filter(([, items]) => items.length).map(([meal, items]) => <p key={meal}><strong>{({ breakfast: 'Café da manhã', morningSnack: 'Lanche da manhã', lunch: 'Almoço', afternoonSnack: 'Lanche da tarde', dinner: 'Jantar', eveningSnack: 'Ceia' } as Record<string, string>)[meal]}:</strong> {items.join('; ')}</p>)}
        </section>)}
      </details>)}
    </Modal>
    <CreateMealPlanModal isOpen={isModalOpen} onClose={closeEditor} patientId={patientId} initialPlan={initialPlan} onSave={save} />
    {pdf && <PDFSendModal isOpen initialMode="save" onClose={() => setPdf(null)} pdfBlob={pdf.blob} fileName={pdf.fileName} patientName={patientName(pdf.patientId)} onSend={async message => {
      if (!user) throw new Error('Entre novamente para enviar o PDF.');
      await sendPDFToPatient(pdf.patientId, user.uid, 'mealPlan', pdf.fileName, pdf.blob, message, true);
    }} />}
  </div>;
};
